// src/components/employee/MyAppeals.js
import React from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import AppealSubjectDetails from "../common/AppealSubjectDetails";
import { getAppealLevelLabel } from "../../utils/appealCycle";
import "./MyAppeals.css";

const typeLabels = {
  penalty: "📝 تظلم على جزاء",
  request: "📋 تظلم على طلب مرفوض",
};

export default function MyAppeals() {
  const { user } = useAuth();
  const { appeals, employeePenalties, allPenalties } = useData();
  const penalties = allPenalties?.length ? allPenalties : employeePenalties || [];

  const myEmployeeId = user?.employeeId || null;

  // ✅ فلترة التظلمات بتاعت الموظف الحالي فقط
  const myAppeals = appeals.filter((appeal) => {
    if (myEmployeeId) {
      return String(appeal.employeeId) === String(myEmployeeId);
    }
    if (user?.code) {
      return appeal.employeeCode === user.code;
    }
    return false;
  });

  // ✅ ترتيب التظلمات من الأحدث للأقدم
  const sortedAppeals = [...myAppeals].sort(
    (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
  );

  function getStatusBadgeClass(status) {
    if (status?.includes("approved")) return "ess-status-approved";
    if (status?.includes("rejected")) return "ess-status-rejected";
    return "ess-status-pending";
  }

  function getCurrentLevelInfo(appeal) {
    const level = appeal.currentLevel;
    const levelData = appeal.levels?.[level];

    if (!levelData) {
      return { text: "قيد المراجعة", class: "pending" };
    }

    if (levelData.status === "approved") {
      return { text: `✅ موافقة ${getAppealLevelLabel(level, appeal)}`, class: "approved" };
    }
    if (levelData.status === "rejected") {
      return { text: `❌ رفض ${getAppealLevelLabel(level, appeal)}`, class: "rejected" };
    }
    if (levelData.status === "pending") {
      return {
        text: `⏳ قيد المراجعة - ${getAppealLevelLabel(level, appeal)}`,
        class: "pending",
      };
    }
    return { text: "⏳ قيد المراجعة", class: "pending" };
  }

  // ✅ دالة مساعدة لتحديد الحالة النهائية
  function getFinalStatus(appeal) {
    // لو فيه finalStatus محدد
    if (appeal.finalStatus) {
      return {
        text:
          appeal.finalStatus === "approved"
            ? "✅ تمت الموافقة النهائية"
            : "❌ رفض نهائي",
        class: appeal.finalStatus === "approved" ? "approved" : "rejected",
      };
    }

    // لو لسه فيه مستويات قيد المراجعة
    const pendingLevels = Object.entries(appeal.levels || {}).filter(
      ([key, val]) => val?.status === "pending",
    );

    if (pendingLevels.length > 0) {
      const currentLevel = pendingLevels[0][0];
      return {
        text: `⏳ قيد المراجعة - ${getAppealLevelLabel(currentLevel, appeal) || currentLevel}`,
        class: "pending",
      };
    }

    // لو كل المستويات اترد عليها
    const allApproved = Object.values(appeal.levels || {}).every(
      (val) => val?.status === "approved" || val?.status === null,
    );

    if (allApproved) {
      return { text: "✅ تمت الموافقة", class: "approved" };
    }

    return { text: "⏳ قيد المراجعة", class: "pending" };
  }

  return (
    <>
      <div className="ess-home-header">
        <h1 className="ess-home-title">📋 تظلماتي</h1>
        <p className="ess-home-subtitle">متابعة حالة التظلمات المقدمة</p>
      </div>

      <div className="ess-table-card">
        <h2 className="ess-table-title">قائمة التظلمات</h2>

        {sortedAppeals.length === 0 ? (
          <div className="ess-empty-state">
            <p className="ess-empty-message">🎉 لا توجد تظلمات مقدمة حالياً</p>
            <p className="ess-empty-subtitle">
              يمكنك تقديم تظلم من صفحة الرئيسية عند الضغط على "تقديم تظلم" بجانب
              أي جزاء أو طلب مرفوض
            </p>
          </div>
        ) : (
          <table className="ess-table">
            <thead>
              <tr>
                <th>#</th>
                <th>نوع التظلم</th>
                <th>تفاصيل الواقعة</th>
                <th>سبب التظلم</th>
                <th>تاريخ التقديم</th>
                <th>الحالة الحالية</th>
                <th>الرد</th>
              </tr>
            </thead>
            <tbody>
              {sortedAppeals.map((appeal, index) => {
                const finalStatus = getFinalStatus(appeal);
                const levelInfo = getCurrentLevelInfo(appeal);

                return (
                  <tr key={appeal.id}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="ess-appeal-type">
                        {typeLabels[appeal.type] || appeal.type}
                      </span>
                    </td>
                    <td>
                      <AppealSubjectDetails
                        appeal={appeal}
                        penalties={penalties}
                        variant="compact"
                      />
                    </td>
                    <td>
                      <span className="ess-appeal-reason" title={appeal.reason}>
                        {appeal.reason?.length > 40
                          ? appeal.reason.substring(0, 40) + "..."
                          : appeal.reason}
                      </span>
                    </td>
                    <td>
                      {new Date(appeal.submittedAt).toLocaleDateString(
                        "ar-EG",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </td>
                    <td>
                      <span
                        className={`ess-status-badge ess-status-${finalStatus.class}`}
                      >
                        {finalStatus.text}
                      </span>
                    </td>
                    <td>
                      {appeal.levels?.[appeal.currentLevel]?.response ? (
                        <span
                          className="ess-response-text"
                          title={appeal.levels[appeal.currentLevel].response}
                        >
                          {appeal.levels[appeal.currentLevel].response.length >
                          25
                            ? appeal.levels[
                                appeal.currentLevel
                              ].response.substring(0, 25) + "..."
                            : appeal.levels[appeal.currentLevel].response}
                        </span>
                      ) : (
                        <span className="ess-no-response">
                          — لا يوجد رد بعد —
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* تفاصيل إضافية */}
      {sortedAppeals.length > 0 && (
        <div className="ess-info-box">
          <h3>📋 معلومات عن التظلمات:</h3>
          <ul>
            <li>
              التظلم يمشي حسب السلسلة الإدارية للموظف حتى قرار الموارد البشرية
            </li>
            <li>يتم إشعارك بكل تحديث على حالة التظلم</li>
            <li>يمكنك تقديم تظلم واحد فقط على كل جزاء أو طلب مرفوض</li>
            <li>
              ⏳ <strong>قيد المراجعة:</strong> التظلم لسه تحت المراجعة
            </li>
            <li>
              ✅ <strong>تمت الموافقة:</strong> تم قبول التظلم وإلغاء
              الجزاء/الرفض
            </li>
            <li>
              ❌ <strong>مرفوض:</strong> تم رفض التظلم والحالة كما هي
            </li>
          </ul>
        </div>
      )}
    </>
  );
}
